import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { APP_NAME, roleLabel, titleOf } from "@/config";
import NotificationBell from "@/components/NotificationBell";
import { Avatar } from "@/components/common";
import {
  LayoutDashboard, Users, FolderGit2, Target, Wallet, Gauge, Activity, ShieldCheck, Settings2, BookOpen, Terminal,
  CirclePlus, Building2, Server, Coins, Moon, Sun, LogOut, FileCheck, Github, Search, ShoppingBag, Menu, X, ScanSearch,
  ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, Mail, FileText, MessageCircleQuestion,
} from "lucide-react";
import { useFlags } from "@/lib/flags";
import { useCompanyName } from "@/lib/company";
import { KIND_LABELS, kindOf } from "@/lib/workKinds";
import { DeletionBanner } from "@/components/AccountDeletion";
import { GraphiteScope } from "@/components/Graphite";
import Assistant from "@/components/Assistant";
import { useAssistant } from "@/lib/assistant";

// The handoff's sidebar (design_handoff_crewbench, Global shell). `section` groups items under a heading that folds;
// `badge` names the count shown on the right (what waits on the founder: change reports they haven't opened, memory
// proposals, full budgets; or the admin's offline runners); `flag` hides an item behind a feature flag; `gap` sets an
// item 12px apart (Settings, on its own after the sections). `action: "assistant"` opens and closes the founder's
// assistant beside the page (components/Assistant.jsx) instead of going to a page.
const NAV = {
  owner: [
    { to: "/founder", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/founder/messages", label: "Messages", icon: Mail, badge: "messages" },
    { action: "assistant", label: "Assistant", icon: MessageCircleQuestion, flag: "documents" },
    { section: "Work", items: [
      { to: "/founder/objectives", label: "Objectives", icon: Target },
      { to: "/founder/projects", label: "Projects", icon: FolderGit2 },
      { to: "/founder/documents", label: "Documents", icon: FileText, flag: "documents" },
      { to: "/founder/team", label: "Team", icon: Users },
      { to: "/founder/memory", label: "Shared memory", icon: BookOpen, badge: "memory" },
    ] },
    { section: "Review", items: [
      { to: "/founder/reports", label: "Change reports", icon: FileCheck, badge: "reports" },
      { to: "/founder/activity", label: "Activity", icon: Activity },
      { to: "/founder/audit", label: "Audit log", icon: ShieldCheck },
      { to: "/founder/investigate", label: "Investigate", icon: ScanSearch, flag: "investigate" },
    ] },
    { section: "Money", items: [
      { to: "/founder/budgets", label: "Budgets", icon: Wallet, badge: "budgets" },
      { to: "/founder/usage", label: "Usage", icon: Gauge },
      { to: "/founder/store", label: "Store", icon: ShoppingBag, flag: "store" },
    ] },
    { to: "/founder/settings", label: "Settings", icon: Settings2, gap: true },
  ],
  coder: [
    { to: "/team", label: "My sessions", icon: Terminal, end: true },
    { to: "/team/new", label: "New session", icon: CirclePlus },
    { to: "/team/messages", label: "Messages", icon: Mail, badge: "messages" },
  ],
  // investors (FEATURE_INVESTORS): their read-only view and Messages with the founders
  viewer: [
    { to: "/investor", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/investor/messages", label: "Messages", icon: Mail, badge: "messages" },
  ],
  platform_admin: [
    { to: "/admin/companies", label: "Companies", icon: Building2 },
    { to: "/admin/runners", label: "Runners", icon: Server, badge: "offline" },
    { to: "/admin/sessions", label: "Sessions", icon: Search },
    { to: "/admin/models", label: "Models and prices", icon: Coins },
    { to: "/admin/github-app", label: "GitHub App", icon: Github },
    { to: "/admin/store", label: "Store", icon: ShoppingBag, flag: ["store", "provisioning"] },
  ],
};

// test ids as they were before the handoff's renames ("Models & Prices" became "Models and prices")
const testid = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-").replace("-and-", "-&-")}`;

// What the sidebar remembers in this browser: narrowed to its icons, and which sections are folded. A convenience:
// without storage (a private window) it opens wide with every section open.
const RAIL_KEY = "cb_nav_rail";
const FOLDED_KEY = "cb_nav_folded";
const recall = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); } catch { return fallback; }
};
const keep = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* not kept */ } };
const isHere = (it, pathname) => (it.end ? pathname === it.to : pathname === it.to || pathname.startsWith(`${it.to}/`));

const BADGE = "min-w-5 h-[18px] px-1.5 rounded bg-foreground text-background font-sans text-xs leading-[18px] font-semibold text-center tracking-normal tabular-nums";

// One link. Narrowed (`rail`, from md up), it shows its icon only, the label kept for screen readers and as a tooltip,
// and its count as a small badge on the icon's corner.
const itemLook = (on, it, rail) => cn(
  "relative flex items-center justify-between gap-2 px-3 py-1.5 rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
  rail && "md:justify-center md:px-0 md:h-9",
  it.gap && "mt-3",
  on ? "bg-gr-hover-strong text-foreground font-semibold" : "text-muted-foreground hover:bg-gr-hover hover:text-foreground");

function NavItem({ it, rail, count, onAction, pressed }) {
  const inside = (
    <>
      <span className="flex items-center gap-2.5 min-w-0"><it.icon className="w-4 h-4 shrink-0" /><span className={cn(rail && "md:sr-only")}>{it.label}</span></span>
      {count > 0 && (
        <span className={cn(BADGE, rail && "md:absolute md:top-0 md:right-0 md:min-w-4 md:h-4 md:px-1 md:text-[10px] md:leading-4")}
          data-testid={`nav-badge-${it.badge}`}>{count}</span>
      )}
    </>
  );
  if (it.action) return (
    <button type="button" onClick={onAction} aria-pressed={pressed} data-testid={testid(it.label)} title={rail ? it.label : undefined}
      className={cn(itemLook(pressed, it, rail), "text-start")}>{inside}</button>
  );
  return (
    <NavLink to={it.to} end={it.end} data-testid={testid(it.label)} title={rail ? it.label : undefined}
      className={({ isActive }) => itemLook(isActive, it, rail)}>{inside}</NavLink>
  );
}

function useBadges(role) {
  // GET /overview/counts works out the founder's waiting list; polled slowly (docs/DESIGN_HANDOFF_PLAN.md section 4)
  const counts = useQuery({
    queryKey: ["overview-counts"], enabled: role === "owner", refetchInterval: 30000,
    queryFn: async () => (await api.get("/overview/counts")).data,
  });
  const admin = useQuery({
    queryKey: ["admin-runners"], enabled: role === "platform_admin", refetchInterval: 30000,
    queryFn: async () => (await api.get("/admin/runners")).data,
  });
  // the mailbox's messages sent to them that they haven't opened (backend/mailbox_routes.py)
  const mailbox = useQuery({
    queryKey: ["mailbox-unread"], enabled: ["owner", "coder", "viewer"].includes(role), refetchInterval: 30000,
    queryFn: async () => (await api.get("/mailbox/unread")).data,
  });
  const messages = mailbox.data?.unread || 0;
  if (role === "owner") return { ...(counts.data || {}), messages };
  if (role === "coder" || role === "viewer") return { messages };
  if (role === "platform_admin") return { offline: (admin.data?.runners || []).filter((r) => r.status === "offline").length };
  return {};
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const flags = useFlags();
  const badges = useBadges(user?.role);
  const shown = (it) => !it.flag || [it.flag].flat().some((f) => flags[f]);
  const items = (NAV[user?.role] || [])
    .map((it) => (it.section ? { ...it, items: it.items.filter(shown) } : it))
    .filter((it) => (it.section ? it.items.length > 0 : shown(it)));
  const company = useCompanyName();
  // a coder's line names their title (Team member or Independent professional) and their kind of work, which picks
  // their sessions' view (lib/workKinds.js): "Team member · Code · Kestrel"
  const role = user?.role === "coder" ? [titleOf(user), KIND_LABELS[kindOf(user)], company].filter(Boolean).join(" · ")
    : ["owner", "viewer"].includes(user?.role) && company ? `${roleLabel(user.role)} · ${company}` : roleLabel(user?.role);
  // below md the sidebar slides over the page from a top bar; it closes on a link, the backdrop or Escape
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const waiting = Object.values(badges).reduce((a, n) => a + (n || 0), 0);
  // narrowed to icons (from md up), and folded sections; a section opens again when you go to one of its pages
  const [rail, setRail] = useState(() => recall(RAIL_KEY, false) === true);
  const [folded, setFolded] = useState(() => new Set(recall(FOLDED_KEY, [])));
  const toggleRail = () => setRail((r) => { keep(RAIL_KEY, !r); return !r; });
  const toggleSection = (name) => setFolded((was) => {
    const next = new Set(was);
    if (next.has(name)) next.delete(name); else next.add(name);
    keep(FOLDED_KEY, [...next]);
    return next;
  });
  useEffect(() => {
    const here = items.find((it) => it.section && it.items.some((x) => isHere(x, pathname)));
    if (here && folded.has(here.section)) toggleSection(here.section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  const assistant = useAssistant();
  const mark = <div className="w-6 h-6 rounded-md bg-foreground text-background grid place-items-center font-display font-bold text-[13px]">C</div>;

  return (
    <GraphiteScope className="min-h-screen flex bg-background text-foreground">
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-12 flex items-center gap-2 px-2 border-b bg-background" data-testid="mobile-bar">
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Menu" aria-expanded={open} data-testid="nav-open"><Menu /></Button>
        {mark}<span className="font-display font-semibold text-[15px] tracking-[0.02em]">{APP_NAME}</span>
        {waiting > 0 && <span className="ml-auto mr-2 min-w-5 h-[18px] px-1.5 rounded bg-foreground text-background text-xs leading-[18px] font-semibold text-center tabular-nums">{waiting}</span>}
      </div>
      {open && <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} aria-hidden="true" />}
      {/* the brand and the account stay put; only the menu between them scrolls (h-dvh: a phone's visible height) */}
      <aside className={cn("w-56 shrink-0 h-screen h-dvh overflow-hidden flex flex-col border-r bg-background",
        "fixed inset-y-0 left-0 z-50 motion-safe:transition-[transform,width] motion-safe:duration-200 md:sticky md:top-0 md:z-auto md:translate-x-0 md:visible",
        rail && "md:w-[60px]",
        open ? "translate-x-0" : "-translate-x-full invisible")} data-testid="sidebar" data-rail={rail ? "true" : "false"}>
        <div className={cn("h-[49px] shrink-0 flex items-center gap-2 px-4 border-b", rail && "md:px-0 md:justify-center")}>
          <span className={cn("flex items-center gap-2 min-w-0", rail && "md:hidden")}>
            {mark}<span className="font-display font-semibold text-[15px] tracking-[0.02em]">{APP_NAME}</span>
          </span>
          <Button variant="ghost" size="icon" className={cn("hidden md:inline-flex text-muted-foreground hover:text-foreground", !rail && "ml-auto -mr-2")}
            onClick={toggleRail} aria-label={rail ? "Widen the sidebar" : "Narrow the sidebar to icons"} aria-pressed={rail}
            title={rail ? "Widen the sidebar" : "Narrow the sidebar to icons"} data-testid="sidebar-rail">
            {rail ? <PanelLeftOpen /> : <PanelLeftClose />}
          </Button>
          <Button variant="ghost" size="icon" className="md:hidden ml-auto -mr-2" onClick={() => setOpen(false)} aria-label="Close the menu"><X /></Button>
        </div>
        <nav className={cn("flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-0.5 p-3 font-display text-[13px] leading-5 tracking-[0.02em]",
          rail && "md:px-2.5")}>
          {items.map((it) => {
            if (it.action) return <NavItem key={it.action} it={it} rail={rail} pressed={assistant.open}
              onAction={() => { setOpen(false); assistant.toggle(); }} />;
            if (!it.section) return <NavItem key={it.to} it={it} rail={rail} count={it.badge ? badges[it.badge] : 0} />;
            const isFolded = folded.has(it.section);
            const id = `nav-section-${it.section.toLowerCase()}`;
            const waitingHere = it.items.reduce((a, x) => a + (x.badge ? badges[x.badge] || 0 : 0), 0);
            return (
              <div key={it.section} className="flex flex-col gap-0.5" data-testid={id}>
                <button type="button" onClick={() => toggleSection(it.section)} aria-expanded={!isFolded} aria-controls={id + "-items"}
                  className={cn("flex items-center justify-between gap-2 mt-2 px-3 py-1.5 rounded-md font-sans text-xs uppercase tracking-[0.09em] text-muted-foreground transition-colors hover:text-foreground hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                    rail && "md:hidden")}>
                  <span>{it.section}</span>
                  <span className="flex items-center gap-1.5">
                    {isFolded && waitingHere > 0 && <span className={BADGE}>{waitingHere}</span>}
                    {isFolded ? <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
                  </span>
                </button>
                {rail && <div className="hidden md:block mx-1.5 my-2 border-t" aria-hidden="true" />}
                {/* folded: hidden, except as icons when narrowed (a narrowed sidebar has no headings to open) */}
                <div id={id + "-items"} className={cn("flex flex-col gap-0.5", isFolded && "hidden", isFolded && rail && "md:flex")}>
                  {it.items.map((x) => <NavItem key={x.to} it={x} rail={rail} count={x.badge ? badges[x.badge] : 0} />)}
                </div>
              </div>
            );
          })}
        </nav>
        <div className={cn("shrink-0 p-3 border-t flex flex-col gap-2.5", rail && "md:px-2.5")}>
          <Link to="/account" title="Account and passkeys" data-testid="account-link"
            className={cn("flex items-center gap-2.5 px-1 rounded-md hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
              rail && "md:justify-center md:px-0")}>
            <Avatar name={user?.name || user?.email} size={28} />
            <span className={cn("flex flex-col min-w-0", rail && "md:sr-only")}>
              <span className="font-semibold truncate">{user?.name || user?.email}</span>
              <span className="text-sm text-muted-foreground truncate" title={user?.role === "coder" ? role : undefined}
                data-testid="sidebar-role">{role}</span>
            </span>
          </Link>
          {/* narrowed: only the avatar stays (owner, 2026-10-13); the phone menu always has the buttons */}
          <div className={cn("flex gap-2", rail && "md:hidden")}>
            <Button variant="outline" className="flex-1 px-0" onClick={toggle} data-testid="theme-toggle"
              aria-label={theme === "dark" ? "Light theme" : "Dark theme"} title={theme === "dark" ? "Light theme" : "Dark theme"}>
              {theme === "dark" ? <Sun /> : <Moon />}
            </Button>
            <NotificationBell />
            <Button variant="outline" className="flex-1 px-0" data-testid="logout-btn" aria-label="Sign out" title="Sign out"
              onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut />
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-4 pt-[76px] pb-10 md:px-8 md:pt-7">
        <DeletionBanner />{children}
      </main>
      <Assistant />
    </GraphiteScope>
  );
}
